import { z } from 'zod'

import { Pagination } from 'app/types/app/request'
import { Product, ProductBinding } from 'app/types/catalogue/product'
import { MachineBinding } from 'app/types/machines/machine'
import { BaseOrder } from 'app/types/operations/order'
import { KonviveBinding } from 'app/types/users/konvive'

/** Status of the status of the sale. */
export const StatusSale = z.enum(['ALL', 'CORRECT', 'PROBLEM'])
export type StatusSale = z.infer<typeof StatusSale>

/** Payment methods availables. */
export const PaymentMethodSale = z.enum([
    'CASH',
    'CREDIT_CARD',
    'KONVIVES',
    'MEAL_VOUCHER',
    'PRIVATE_CARD',
])

export type PaymentMethodSale = z.infer<typeof PaymentMethodSale>

/** Status of a sale dispute. */
export const StatusDisputeSale = z.enum([
    'CONTACTED',
    'DISPUTED',
    'IN_PROGRESS',
    'REFUNDED',
    'REFUSED',
])
export type StatusDisputeSale = z.infer<typeof StatusDisputeSale>

/** Type of a sale dispute. */
export const TypeDisputeSale = z.enum([
    'CLOSED_LOCKER',
    'MENU_NOT_TAKEN',
    'NUMBER_PRODUCTS',
    'OTHER',
    'WRONG_PRICE',
    'WRONG_PRODUCT',
])

export type TypeDisputeSale = z.infer<typeof TypeDisputeSale>

export const ICoupon = z.object({
    id: z.string(),
    amount: z.number().positive(),
    is_multiple_use: z.boolean(),
})

export type ICoupon = z.infer<typeof ICoupon>

export const CodeSale = z.object({
    id: z.string(),
    amount: z.number().positive(),
    is_percentage: z.boolean(),
})

export type CodeSale = z.infer<typeof CodeSale>

/** Product sold in a sale, returned by the API. */
export const ProductSale = Product.pick({
    id: true,
    name: true,
    price: true,
    tva: true,
    client_serial: true,
    supplier: true,
    purchase_price: true,
}).extend({
    dlc: z.string().date(),
    price_paid: z.number(),
    product: ProductBinding,
    selection: z.string().length(3),
})

export type ProductSale = z.infer<typeof ProductSale>

/** Menu object returned by the API. */
export const MenuSale = z.object({
    id: z.number(),
    name: z.string(),
    price: z.number(),
    unit: z.string(),
    tva: z.number(),
    employer_participation: z.boolean(),
    products: ProductSale.array(),
})

export type MenuSale = z.infer<typeof MenuSale>

/** Discount object returned by the API */
export const DiscountSale = z.object({
    id: z.number(),
    name: z.string(),
    price: z.number(),
    unit: z.string(),
    tva: z.number(),
    products: ProductSale.array(),
})

export type DiscountSale = z.infer<typeof DiscountSale>

/** Cart discount object returned by the API */
export const CartDiscountSale = z.object({
    id: z.number().positive(),
    name: z.string().min(1),
    price: z.number().nonnegative(),
    discount_amount: z.number().positive(),
    is_participation: z.boolean(),
    menus: MenuSale.array(),
    discounts: DiscountSale.array(),
    products: ProductSale.array(),
})

export type CartDiscountSale = z.infer<typeof CartDiscountSale>

/** Consumer dispute about the sale. */
export const DisputeSale = z.object({
    datetime: z.string().datetime({ offset: true }),
    email: z.string().email(),
    type: TypeDisputeSale,
    products: Product.array(),
})

/** The order in a sale. */
export const OrderSale = BaseOrder.pick({
    id: true,
    price: true,
})

export type OrderSale = z.infer<typeof OrderSale>

export const ReasonProductNotDistributedSale = z.enum([
    'CONFIGURATION',
    'CONNECTION',
    'DOOR',
    'ELEVATOR',
    'ELIMINATED',
    'EXPIRED',
    'MOTOR',
    'QUEUE',
    'RESET',
    'STOCK',
    'TOKEN',
    'UNKNOWN',
])

export type ReasonProductNotDistributedSale = z.infer<
    typeof ReasonProductNotDistributedSale
>

const ProductNotDistributedSale = z.object({
    id: z.number(),
    product: ProductBinding,
    selection: z.string().length(3),
    reason: ReasonProductNotDistributedSale,
})

export type ProductNotDistributedSale = z.infer<
    typeof ProductNotDistributedSale
>

export const Sale = z.object({
    id: z.number(),
    date_time: z.string().datetime({ offset: true }),
    acceptation_date: z.string().datetime({ offset: true }),
    terminal: MachineBinding,
    total: z.number().nonnegative(),
    participation: z.number().nonnegative(),
    konvive: KonviveBinding.optional(),
    payment_method: PaymentMethodSale,
    card_type: z.string().optional(),
    discount_amount: z.number().positive(),
    max_auth: z.number().positive(),
    sale_ok: z.boolean(),
    pspReference: z.string().optional(),
    /** The amount paid by a konvives with a credit card. */
    amount_credit_card: z.number().nonnegative(),
    /** The amount paid by a konvives with a konvives wallet. */
    amount_konvives_wallet: z.number().nonnegative(),
    pan: z.string(),
    status_dispute: StatusDisputeSale.optional(),
    dispute_sale: DisputeSale.optional(),
    orders: OrderSale.array(),
    cart_discount: CartDiscountSale.optional(),
    menus: MenuSale.array(),
    discounts: DiscountSale.array(),
    products: ProductSale.array(),
    products_not_distributed: ProductNotDistributedSale.array(),
    icoupons: ICoupon.array(),
    codes: CodeSale.array(),
})

export type Sale = z.infer<typeof Sale>

/** Sale returned by the dispute actions (contact, refuse), reduced to the fields used. */
export const DisputeActionSale = Sale.pick({
    id: true,
}).extend({
    status_dispute: StatusDisputeSale,
})

export type DisputeActionSale = z.infer<typeof DisputeActionSale>

/** Sale object returned by the API for the sales list. */
export const SaleList = Sale.pick({
    id: true,
    date_time: true,
    terminal: true,
    total: true,
    amount_credit_card: true,
    amount_konvives_wallet: true,
    participation: true,
    payment_method: true,
    sale_ok: true,
    status_dispute: true,
    icoupons: true,
    pan: true,
    pspReference: true,
    konvive: true,
})

export type SaleList = z.infer<typeof SaleList>

/** Sales list response returned by the API. */
export const SalesRequest = Pagination.extend({
    results: SaleList.array(),
    min_total: z.number().positive(),
    max_total: z.number().positive(),
    ca_total: z.number().positive(),
    sales_total: z.number().positive(),
    products_total: z.number().positive(),
})

export type SalesRequest = z.infer<typeof SalesRequest>
